'use strict';

/**
 * Single-project **PipelineStatus** data path: loads append-only facts, maps to graph node view-models (`PipelineStatusStage[]`),
 * derives `publishDisabled`. No Redux pipeline slice, no legacy progress UI, no SignalR payload in state (hub only invalidates RTK tags).
 *
 * Graph input: `stages` is one node per graph definition for the **explicit** `flowScenarioKey` (see `getPipelineStatusGraphDefinitionForFlow`) with stable `id` / `dependsOn`.
 * Facts bind by `factMatch`: `scope` + `workflow` + `pipeline` + `stage` + `service` (see `pipelineStatusAdapter`).
 * (`comparePipelineFactsRecency` in `pipelineStatusAdapter` is deterministic).
 *
 * ## PortalApi `GET …/admin/pipeline-status/projects/{id}/facts`
 *
 * Response body: `{ facts, iterationManifests }` (camelCase). Legacy bare array responses are treated as facts-only.
 *
 * **`ENABLE_PIPELINE_STATUS`** (see `@/config/env.config`) must be on for this hook to query PortalApi. When it is off, the hook
 * skips RTK and returns empty stages (panel is not mounted from `Project.jsx`).
 *
 * When **`USE_PIPELINE_STATUS_API_MOCKS`** is **`true`** (see `@/config/env.config`),
 * the Portal RTK `baseQuery` returns fixture JSON for admin/pipeline-status/* GETs and **no network request** is made. Otherwise
 * responses come from the live Portal admin pipeline-status routes.
 *
 * With mocks on, **`PIPELINE_STATUS_MOCK_SCENARIO`** may override the flow key; when unset, the scenario follows `ProjectDescription.pipeline` like live mode (see `requirePipelineStatusMockScenarioFromEnv`). Use {@link useResolvedPipelineStatusFlowScenario} so the graph matches the fixture rows. With mocks off, pass the key from {@link pipelineStatusFlowScenarioFromProjectPipeline} using `ProjectDescription.pipeline`.
 *
 * Fact rows use backend `status` values `Started` | `Completed` | `Failed` (see `pipelineStatusAdapter.js`).
 *
 * Requires **`canEditUsers`** (PortalApi admin pipeline-status project routes). Callers without that permission should not mount the panel; the hook skips RTK when permission is absent.
 */

import { useMemo } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { ENABLE_PIPELINE_STATUS, USE_PIPELINE_STATUS_API_MOCKS } from '@/config/env.config';
import { usePipelineStatusFactsQuery } from '@/redux/services/portal/projects.api';
import { usePermissions } from '@/hooks/permissions.hook';
import { CAN_EDIT_USERS } from '@/security/permissionTypes';
import { getPipelineStatusGraphDefinitionForFlow } from './pipelineStatusGraphDefinition';
import {
	pipelineStatusFlowScenarioFromProjectPipeline,
	requirePipelineStatusFlowScenarioKey,
} from './pipelineStatusFlowScenarios';
import { requirePipelineStatusMockScenarioFromEnv } from './mocks/pipelineStatusFacts.mock';
import { buildPipelineStatusGraphViewModel, pipelineStatusShouldBlockPublish } from './pipelineStatusAdapter';

/**
 * Resolves which pipeline-status graph scenario to use: with API mocks, the env scenario (must match mock facts); otherwise `ProjectDescription.pipeline`.
 *
 * @param {unknown} projectPipeline Portal `ProjectDescription.pipeline` when mocks are off.
 * @returns {string}
 */
export function useResolvedPipelineStatusFlowScenario(projectPipeline) {
	return useMemo(() => {
		if (USE_PIPELINE_STATUS_API_MOCKS) {
			return requirePipelineStatusMockScenarioFromEnv(projectPipeline);
		}
		return pipelineStatusFlowScenarioFromProjectPipeline(projectPipeline);
	}, [projectPipeline]);
}

/**
 * Normalizes RTK / JSON rows into `PipelineStatusFact[]` for the adapter.
 * @param {unknown[] | undefined} rows
 * @returns {import('./pipelineStatusTypes').PipelineStatusFact[]}
 */
export function mapApiRowsToPipelineStatusFacts(rows) {
	if (!Array.isArray(rows)) return [];
	return rows.map((r) => {
		const row = /** @type {Record<string, unknown>} */ (r);
		return {
			id: row.id != null ? String(row.id) : '',
			scope: row.scope != null ? String(row.scope) : '',
			workflow: row.workflow != null ? String(row.workflow) : '',
			subjectId: row.subjectId != null ? String(row.subjectId) : '',
			parentWorkflow: row.parentWorkflow != null ? String(row.parentWorkflow) : null,
			parentSubjectId: row.parentSubjectId != null ? String(row.parentSubjectId) : null,
			pipeline: row.pipeline != null ? String(row.pipeline) : '',
			stage: row.stage != null ? String(row.stage) : '',
			status: row.status != null ? String(row.status) : '',
			occurredAt: row.occurredAt != null ? String(row.occurredAt) : undefined,
			correlationId: row.correlationId != null ? String(row.correlationId) : null,
			service: row.service != null ? String(row.service) : undefined,
			serviceVersion: row.serviceVersion != null ? String(row.serviceVersion) : undefined,
			iterationDimension:
				row.iterationDimension != null && String(row.iterationDimension).trim() !== ''
					? String(row.iterationDimension)
					: null,
			iterationValue:
				row.iterationValue != null && String(row.iterationValue).trim() !== ''
					? String(row.iterationValue)
					: null,
			detailsJson:
				row.detailsJson != null && String(row.detailsJson).trim() !== '' ? String(row.detailsJson) : null,
		};
	});
}

/**
 * @param {unknown[] | undefined} rows
 * @returns {import('./pipelineStatusTypes').PipelineStatusIterationManifest[]}
 */
export function mapApiRowsToPipelineStatusIterationManifests(rows) {
	if (!Array.isArray(rows)) return [];
	return rows.map((r) => {
		const row = /** @type {Record<string, unknown>} */ (r);
		const rawVals = row.iterationValues ?? row.IterationValues;
		const iterationValues = Array.isArray(rawVals) ? rawVals.map((v) => String(v)) : [];
		return {
			id: row.id != null ? String(row.id) : '',
			correlationId: row.correlationId != null ? String(row.correlationId) : '',
			scope: row.scope != null ? String(row.scope) : '',
			workflow: row.workflow != null ? String(row.workflow) : '',
			pipeline: row.pipeline != null ? String(row.pipeline) : '',
			parentStage: row.parentStage != null ? String(row.parentStage) : '',
			parentService:
				row.parentService != null && String(row.parentService).trim() !== ''
					? String(row.parentService)
					: null,
			subjectId: row.subjectId != null ? String(row.subjectId) : '',
			parentWorkflow:
				row.parentWorkflow != null && String(row.parentWorkflow).trim() !== ''
					? String(row.parentWorkflow)
					: null,
			parentSubjectId:
				row.parentSubjectId != null && String(row.parentSubjectId).trim() !== ''
					? String(row.parentSubjectId)
					: null,
			iterationDimension: row.iterationDimension != null ? String(row.iterationDimension) : '',
			iterationValues,
			createdAtUtc: row.createdAtUtc != null ? String(row.createdAtUtc) : '',
			sourceService: row.sourceService != null ? String(row.sourceService) : '',
		};
	});
}

/**
 * Normalizes `GET …/facts` JSON into facts + iteration manifests (supports legacy array-only payloads).
 * @param {unknown} data
 * @returns {{
 *   facts: import('./pipelineStatusTypes').PipelineStatusFact[],
 *   iterationManifests: import('./pipelineStatusTypes').PipelineStatusIterationManifest[],
 * }}
 */
export function mapApiPipelineStatusSnapshotPayload(data) {
	if (data == null) return { facts: [], iterationManifests: [] };
	if (Array.isArray(data)) {
		return { facts: mapApiRowsToPipelineStatusFacts(data), iterationManifests: [] };
	}
	const o = /** @type {Record<string, unknown>} */ (data);
	const factsRaw = o.facts ?? o.Facts;
	const manRaw = o.iterationManifests ?? o.IterationManifests;
	return {
		facts: mapApiRowsToPipelineStatusFacts(Array.isArray(factsRaw) ? /** @type {unknown[]} */ (factsRaw) : []),
		iterationManifests: mapApiRowsToPipelineStatusIterationManifests(
			Array.isArray(manRaw) ? /** @type {unknown[]} */ (manRaw) : [],
		),
	};
}

/**
 * @param {string | undefined} projectId
 * @param {unknown} flowScenarioKey Known flow scenario (e.g. `singleV7ProjectFlow` from `pipelineStatusFlowScenarios`).
 * @returns {{
 *   definition: import('./pipelineStatusTypes').PipelineStatusGraphDefinition,
 *   facts: import('./pipelineStatusTypes').PipelineStatusFact[],
 *   iterationManifests: import('./pipelineStatusTypes').PipelineStatusIterationManifest[],
 *   stages: import('./pipelineStatusTypes').PipelineStatusStage[],
 *   publishDisabled: boolean,
 *   isLoading: boolean,
 *   isFetching: boolean,
 *   error: unknown,
 *   refetch: () => Promise<unknown>,
 * }}
 */
export function useCanAccessProjectPipelineStatus() {
	const { checkUserHasPermissions } = usePermissions();
	return checkUserHasPermissions(CAN_EDIT_USERS);
}

export function usePipelineStatus(projectId, flowScenarioKey) {
	const flowKey = requirePipelineStatusFlowScenarioKey(flowScenarioKey);
	const trackerEnabled = ENABLE_PIPELINE_STATUS;
	const canAccessProjectPipelineStatus = useCanAccessProjectPipelineStatus();

	const queryArg = useMemo(() => {
		if (!trackerEnabled || !canAccessProjectPipelineStatus) return skipToken;
		if (!projectId) return skipToken;
		return projectId;
	}, [trackerEnabled, canAccessProjectPipelineStatus, projectId]);

	const query = usePipelineStatusFactsQuery(queryArg);

	const { facts, iterationManifests } = useMemo(
		() => mapApiPipelineStatusSnapshotPayload(query.data),
		[query.data],
	);

	const definition = useMemo(() => getPipelineStatusGraphDefinitionForFlow(flowKey), [flowKey]);

	const stages = useMemo(
		() => buildPipelineStatusGraphViewModel(definition, facts, iterationManifests, projectId),
		[definition, facts, iterationManifests, projectId],
	);

	const publishDisabled = useMemo(() => pipelineStatusShouldBlockPublish(stages), [stages]);

	return {
		definition,
		facts,
		iterationManifests,
		stages,
		publishDisabled,
		isLoading: !trackerEnabled || !canAccessProjectPipelineStatus ? false : query.isLoading,
		isFetching: !trackerEnabled || !canAccessProjectPipelineStatus ? false : query.isFetching,
		error: !trackerEnabled || !canAccessProjectPipelineStatus ? undefined : query.error,
		refetch: query.refetch,
	};
}
